import React, { useMemo, useState } from 'react';
import { ExternalLink, FileText, Mail, Phone, ShieldCheck, User } from 'lucide-react';
import styles from './CuratedPostulationsSection.module.scss';
import EmptyState from '../Shared/EmptyState.tsx';
import LoadingSpinner from '../Shared/LoadingSpinner.tsx';
import { CuratedJobRequestItem, CuratedJobRequestSummary, CuratedPostulation } from '../../../../types/backoffice.ts';

interface CuratedPostulationsSectionProps {
  jobRequests: CuratedJobRequestItem[];
  jobRequestsLoading: boolean;
  jobRequestsError: string | null;
  selectedJobRequest: CuratedJobRequestSummary | null;
  postulations: CuratedPostulation[];
  postulationsLoading: boolean;
  postulationsError: string | null;
  onSelectJobRequest: (jobRequestId: number) => void;
}

type PostulationFilter = 'all' | 'with' | 'without';

const getPostulationCount = (jobRequest: CuratedJobRequestItem) => jobRequest.postulationCount ?? 0;

const formatDateTime = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('es-AR');
};

const CuratedPostulationsSection: React.FC<CuratedPostulationsSectionProps> = ({
  jobRequests,
  jobRequestsLoading,
  jobRequestsError,
  selectedJobRequest,
  postulations,
  postulationsLoading,
  postulationsError,
  onSelectJobRequest,
}) => {
  const [postulationFilter, setPostulationFilter] = useState<PostulationFilter>('with');
  const selectedId = selectedJobRequest?.id ?? null;
  const totalCount = jobRequests?.length ?? 0;

  const withPostulationsCount = useMemo(
    () => (jobRequests || []).filter((jobRequest) => getPostulationCount(jobRequest) > 0).length,
    [jobRequests]
  );

  const filteredJobRequests = useMemo(() => {
    let list = jobRequests || [];
    if (postulationFilter === 'with') {
      list = list.filter((jobRequest) => getPostulationCount(jobRequest) > 0);
    } else if (postulationFilter === 'without') {
      list = list.filter((jobRequest) => getPostulationCount(jobRequest) === 0);
    }
    if (postulationFilter === 'with') {
      return [...list].sort((a, b) => getPostulationCount(b) - getPostulationCount(a));
    }
    return list;
  }, [jobRequests, postulationFilter]);

  const countLabel = useMemo(() => {
    if (postulationFilter === 'all') {
      return `${totalCount} avisos`;
    }
    return `${filteredJobRequests.length} de ${totalCount} avisos`;
  }, [filteredJobRequests.length, postulationFilter, totalCount]);

  if (jobRequestsLoading) {
    return <LoadingSpinner message="Cargando avisos externos..." />;
  }

  if (jobRequestsError) {
    return <EmptyState title="No pudimos cargar los avisos" message={jobRequestsError} />;
  }

  if (!jobRequests || jobRequests.length === 0) {
    return (
      <EmptyState
        title="No hay avisos externos"
        message="Cuando existan avisos publicados por el usuario ADMIN_USER aparecerán aquí."
      />
    );
  }

  return (
    <div className={styles.section}>
      <div className={styles.header}>
        <h2 className={styles.title}>Postulaciones de avisos externos</h2>
        <span className={styles.count}>{countLabel}</span>
      </div>

      <div className={styles.layout}>
        <div className={styles.listCard}>
          <h3 className={styles.cardTitle}>Avisos curados</h3>
          <div className={styles.filters}>
            <button
              type="button"
              className={`${styles.filterButton} ${postulationFilter === 'with' ? styles.filterActive : ''}`}
              onClick={() => setPostulationFilter('with')}
            >
              Con postulaciones ({withPostulationsCount})
            </button>
            <button
              type="button"
              className={`${styles.filterButton} ${postulationFilter === 'without' ? styles.filterActive : ''}`}
              onClick={() => setPostulationFilter('without')}
            >
              Sin postulaciones
            </button>
            <button
              type="button"
              className={`${styles.filterButton} ${postulationFilter === 'all' ? styles.filterActive : ''}`}
              onClick={() => setPostulationFilter('all')}
            >
              Todos
            </button>
          </div>
          {filteredJobRequests.length === 0 ? (
            <EmptyState
              title={
                postulationFilter === 'with'
                  ? 'Ningún aviso con postulaciones'
                  : postulationFilter === 'without'
                    ? 'Todos los avisos tienen postulaciones'
                    : 'No hay avisos'
              }
              message={
                postulationFilter === 'with'
                  ? 'Cuando un aviso reciba postulaciones aparecerá en este listado.'
                  : postulationFilter === 'without'
                    ? 'No hay avisos sin postulaciones con el filtro actual.'
                    : 'No hay avisos para mostrar.'
              }
            />
          ) : (
          <div className={styles.list}>
            {filteredJobRequests.map((jobRequest) => {
              const postulationCount = getPostulationCount(jobRequest);
              return (
              <button
                key={jobRequest.id}
                type="button"
                className={`${styles.listItem} ${selectedId === jobRequest.id ? styles.listItemActive : ''}`}
                onClick={() => onSelectJobRequest(jobRequest.id)}
              >
                <div className={styles.listItemTop}>
                  <span className={styles.itemId}>#{jobRequest.id}</span>
                  <span
                    className={`${styles.itemCount} ${postulationCount > 0 ? styles.itemCountActive : styles.itemCountEmpty}`}
                  >
                    {postulationCount} postulaciones
                  </span>
                </div>
                <p className={styles.itemDescription}>{jobRequest.description || 'Sin descripción'}</p>
                <div className={styles.itemMeta}>
                  <span>Creado: {formatDate(jobRequest.createdAt)}</span>
                  <span>Vence: {formatDate(jobRequest.activeUntil)}</span>
                </div>
              </button>
              );
            })}
          </div>
          )}
        </div>

        <div className={styles.detailCard}>
          {!selectedJobRequest ? (
            <EmptyState
              title="Seleccioná un aviso"
              message="Elegí un aviso de la lista para ver sus postulaciones."
            />
          ) : (
            <>
              <div className={styles.detailHeader}>
                <h3 className={styles.cardTitle}>Detalle del aviso #{selectedJobRequest.id}</h3>
                <p className={styles.detailDescription}>{selectedJobRequest.description || 'Sin descripción'}</p>
                <div className={styles.detailMeta}>
                  <span>
                    <Mail size={14} />
                    {selectedJobRequest.contactMethod}: {selectedJobRequest.alternativeContact || '—'}
                  </span>
                  <span>
                    <FileText size={14} />
                    Vigencia hasta {formatDate(selectedJobRequest.activeUntil)}
                  </span>
                </div>
              </div>

              {postulationsLoading && <LoadingSpinner message="Cargando postulaciones..." />}
              {!postulationsLoading && postulationsError && (
                <EmptyState title="No pudimos cargar postulaciones" message={postulationsError} />
              )}
              {!postulationsLoading && !postulationsError && postulations.length === 0 && (
                <EmptyState title="Sin postulaciones" message="Este aviso todavía no recibió postulaciones." />
              )}

              {!postulationsLoading && !postulationsError && postulations.length > 0 && (
                <div className={styles.postulationsList}>
                  {postulations.map((postulation) => (
                    <article key={postulation.id} className={styles.postulationCard}>
                      <div className={styles.postulationTop}>
                        <span className={styles.postulationId}>Postulación #{postulation.id}</span>
                        <span className={styles.postulationDate}>{formatDateTime(postulation.appliedAt)}</span>
                      </div>
                      <div className={styles.postulationBody}>
                        <p className={styles.coverLetter}>{postulation.coverLetter || 'Sin carta de presentación.'}</p>
                        {postulation.cvUrl ? (
                          <a
                            href={postulation.cvUrl}
                            target="_blank"
                            rel="noreferrer"
                            className={styles.cvLink}
                          >
                            <ExternalLink size={14} />
                            Ver CV
                          </a>
                        ) : (
                          <span className={styles.cvMissing}>Sin CV disponible</span>
                        )}
                      </div>
                      <div className={styles.applicant}>
                        {postulation.applicant ? (
                          <>
                            <span>
                              <User size={14} />
                              {`${postulation.applicant.name || ''} ${postulation.applicant.last_name || ''}`.trim() || postulation.applicant.username}
                            </span>
                            <span>
                              <Mail size={14} />
                              {postulation.applicant.username || '—'}
                            </span>
                            <span>
                              <Phone size={14} />
                              {postulation.applicant.phone || '—'}
                            </span>
                            <span>
                              <ShieldCheck size={14} />
                              Identidad: {postulation.applicant.identity_validated === true ? 'Validada' : postulation.applicant.identity_validated === false ? 'No validada' : 'Sin dato'}
                            </span>
                          </>
                        ) : (
                          <span>Postulante sin datos asociados</span>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CuratedPostulationsSection;
